import { useEffect } from "react";
import { useAuthStore } from "@/store/authStore";
import { endSession, recentSignOutReason } from "@/lib/auth/endSession";

/** The key `authStore`'s `persist` middleware writes its state under. */
const AUTH_STORAGE_KEY = "everythingcloud-auth";

/**
 * Whether a persisted auth payload still holds a session.
 *
 * The value is whatever zustand's `persist` wrote, so it is parsed
 * defensively: a browser that cleared the key, another origin's write, or a
 * shape change all arrive here as something other than a signed-in state, and
 * all of them mean the same thing — this tab should not stay signed in.
 */
function hasSession(raw: string | null): boolean {
  if (!raw) return false;
  try {
    const parsed = JSON.parse(raw) as { state?: { accessToken?: unknown } };
    return Boolean(parsed?.state?.accessToken);
  } catch {
    return false;
  }
}

/**
 * Ends this tab's session the moment another tab signs out.
 *
 * Signing out revokes the refresh token server-side, but a second tab held its
 * own copy of the access token in memory and went on using it — navigating,
 * filtering, opening resources — because nothing told it anything had changed.
 * `persist` writes the store to local storage, and other tabs never rehydrate
 * from it, so the sign-out was invisible to them.
 *
 * `useRemoteSessionGuard` polls for this, but polling is a backstop, not a
 * mechanism: it leaves up to 30 seconds of authenticated access, and browsers
 * clamp `setInterval` in a background tab to a minute or more — which is
 * exactly where a forgotten second tab sits.
 *
 * The `storage` event fires in every *other* tab of the origin the moment the
 * key changes, needs no new channel, and rides the persistence that already
 * exists. The tab that signed out does not receive its own event, which is why
 * this only ever reacts to someone else's.
 */
export function useCrossTabAuthSync() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) return;

    const onStorage = (event: StorageEvent) => {
      // `key` is null when a tab calls localStorage.clear(); treat that as a
      // sign-out too, since whatever this tab was holding is gone with it.
      // Only this origin's *local* storage. A `sessionStorage.clear()` in a
      // same-origin iframe raises the same null-key event, and reading that as
      // a sign-out would end a perfectly good session.
      if (event.storageArea && event.storageArea !== window.localStorage)
        return;
      if (event.key !== null && event.key !== AUTH_STORAGE_KEY) return;
      if (hasSession(event.newValue)) return;

      // Tear the page down rather than re-rendering it: a protected view that
      // is mid-fetch should not get to finish and paint the data. When the
      // other tab ended for a reason (idle, the session's lifetime), this one
      // says the same thing rather than landing on a bare login page.
      const recorded = recentSignOutReason();
      endSession(recorded?.reason, recorded?.params);
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [isAuthenticated]);
}
